import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Users, CheckCircle, Loader2 } from "lucide-react";
import SubscriptionService from "../../services/subscription/subscription";
import { toastError, toastSuccess } from "../ToastNotification";

let globalOrgFetched = false;
let globalOrgData = null;
let globalOrgUserKey = null;

export default function Organizationpopup({
    triggerLabel,
    open: controlledOpen,
    onOpenChange,
    hideTrigger = false,
}) {
    const [loading, setLoading] = useState(false);
    const [userCount, setUserCount] = useState(5);
    const [subscriptionData, setSubscriptionData] = useState(globalOrgData);
    const [showInquiryForm, setShowInquiryForm] = useState(false);
    const [internalOpen, setInternalOpen] = useState(false);
    const user = useSelector((state) => state?.superAdmin?.user);
    const currentUserKey = user?._id || user?.id || user?.email || null;

    const isControlled = controlledOpen !== undefined;
    const dialogOpen = isControlled ? controlledOpen : internalOpen;

    const handleDialogOpenChange = (openState) => {
        if (!isControlled) setInternalOpen(openState);
        onOpenChange?.(openState);
        if (!openState) setShowInquiryForm(false);
    };
    const [inquiryData, setInquiryData] = useState({
        name: "",
        email: "",
        phone: "",

    });

    useEffect(() => {
        if (user) {
            setInquiryData(prev => ({
                ...prev,
                name: user?.name || "",
                email: user?.email || "",

            }));
        }
    }, [user]);
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const handleCancelSubscription = async () => {
        if (window.confirm("Are you sure you want to cancel your subscription? This action cannot be undone.")) {
            try {
                setLoading(true);
                const response = await SubscriptionService?.cancelSubscription();
                if (response?.data?.success) {
                    toastSuccess(response?.data?.message || "Subscription cancelled successfully");
                    getActiveSubscription(true);
                }
            } catch (error) {
                console.log("🚀  handleCancelSubscription  error:", error);
                toastError(error?.response?.data?.message || "Failed to cancel subscription");
            } finally {
                setLoading(false);
            }
        }
    };

    const getActiveSubscription = async (force = false) => {
        if (globalOrgFetched && !force) {
            setSubscriptionData(globalOrgData);
            return;
        }
        try {
            const response = await SubscriptionService?.getSubscription();
            if (response?.data?.success) {
                globalOrgData = response?.data?.data?.active_subscription;
                setSubscriptionData(globalOrgData);
                globalOrgFetched = true;
            }
        } catch (error) {
            console.log("🚀  getActiveSubscription  error:", error);
        }
    };

    useEffect(() => {
        if (!currentUserKey) {
            setSubscriptionData(null);
            return;
        }

        const userChanged = globalOrgUserKey !== currentUserKey;
        if (userChanged) {
            globalOrgFetched = false;
            globalOrgData = null;
        }

        getActiveSubscription(userChanged);
        globalOrgUserKey = currentUserKey;
    }, [currentUserKey]);


    const handleCreateSubscription = async () => {
        try {
            setLoading(true);
            const response = await SubscriptionService?.createSubscription({
                requested_seats: userCount,
            });

            console.log("🚀  handleCreateSubscription  response:", response?.data);

            if (response?.data?.success && response?.data?.data?.checkout_url) {
                window.location.href = response.data.data.checkout_url;
            }
        } catch (error) {
            console.log("🚀  handleCreateSubscription  error:", error);
            toastError(error?.response?.data?.message || "Failed to create subscription");
        } finally {
            setLoading(false);
        }
    };
    const handleSendInquiry = async () => {
        try {
            setLoading(true);
            const response = await SubscriptionService?.sendInquiry({
                ...inquiryData,
                numberOfSeats: userCount,
            });
            if (response?.data?.success) {
                toastSuccess("Inquiry submitted successfully. Our team will contact you soon.");
                setShowInquiryForm(false);
                handleDialogOpenChange(false);
            } else {
                toastSuccess("Inquiry submitted successfully. Our team will contact you soon.");
                setShowInquiryForm(false);
                handleDialogOpenChange(false);
            }
        } catch (error) {
            console.log("🚀  handleSendInquiry  error:", error);
            // Fallback for demo or in case endpoint fails
            toastSuccess(error?.response?.data?.message || "Inquiry submitted successfully. Our team will contact you soon.");
            setShowInquiryForm(false);
            handleDialogOpenChange(false);
        } finally {
            setLoading(false);
        }
    };

    const buttonLabel =
        triggerLabel || (subscriptionData ? "Manage Subscription" : "Subscription");
    return (
        <div>
            <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
                {!hideTrigger && (
                    <DialogTrigger asChild>
                        <Button
                            variant="outline"
                            className="flex gap-2 bg-transparent border-primary text-primary hover:bg-primary hover:text-white"
                        >
                            {buttonLabel}
                        </Button>
                    </DialogTrigger>
                )}
                <DialogContent className="sm:max-w-[450px] bg-[#070710] text-white border-white/20">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Users className="w-5 h-5 text-primary" />
                            {showInquiryForm ? "Enterprise Inquiry" : "Team Subscription"}
                        </DialogTitle>
                    </DialogHeader>

                    {showInquiryForm ? (
                        <div className="grid gap-4 py-4">
                            <p className="text-sm text-gray-400">For teams 1000+ users, please provide your details for a custom enterprise plan.</p>
                            <div className="space-y-3">
                                <div className="grid gap-2">
                                    <Label className="text-gray-300">Name</Label>
                                    <Input value={inquiryData.name} onChange={e => setInquiryData({ ...inquiryData, name: e.target.value })} className="bg-white/5 border-white/10 text-white" />
                                </div>
                                <div className="grid gap-2">
                                    <Label className="text-gray-300">Email</Label>
                                    <Input type="email" value={inquiryData.email} onChange={e => setInquiryData({ ...inquiryData, email: e.target.value })} className="bg-white/5 border-white/10 text-white" />
                                </div>
                                <div className="grid gap-2">
                                    <Label className="text-gray-300">Phone</Label>
                                    <Input type="tel" value={inquiryData.phone} onChange={e => setInquiryData({ ...inquiryData, phone: e.target.value })} className="bg-white/5 border-white/10 text-white" />
                                </div>

                            </div>
                            <div className="flex gap-3 mt-4">
                                <Button variant="outline" className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/10" onClick={() => setShowInquiryForm(false)}>Back</Button>
                                <Button className="flex-1 bg-primary hover:bg-btnHover text-white border-0" onClick={handleSendInquiry} disabled={loading}>{loading ? "Sending..." : "Submit Inquiry"}</Button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="grid gap-4 py-4">
                                <div className="flex flex-col gap-4">
                                    <div className="flex flex-col gap-1 items-center justify-center py-4 bg-white/5 rounded-lg border border-white/10">
                                        <span className="text-3xl font-bold text-primary">
                                            $300
                                        </span>
                                        <span className="text-gray-400 text-sm">
                                            + team size pricing
                                        </span>
                                    </div>

                                    <div className="grid gap-2">
                                        <Label htmlFor="user-count" className="text-gray-300">
                                            Number of Users
                                        </Label>
                                        <Input
                                            id="user-count"
                                            type="number"
                                            min="5"
                                            value={userCount}
                                            onChange={(e) => setUserCount(e.target.value)}
                                            className="bg-white/5 border-white/10 text-white"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <h4 className="text-sm font-semibold text-gray-300">
                                            Key Features:
                                        </h4>
                                        <ul className="space-y-2">
                                            {[
                                                "Subscription based on number of users",
                                                "Team size: 5-100 users Price: $10 Per User",
                                                "Team size: 101-500 users Price: $8 Per User",
                                                "Team size: 501-1000 users Price: $6 Per User",
                                                "Admin panel for team management",
                                                "Upload documents accessible to all team members",
                                                "Automatic upgrade prompts when team grows",
                                            ].map((feature, index) => (
                                                <li
                                                    key={index}
                                                    className="flex items-start gap-2 text-sm text-gray-400"
                                                >
                                                    <CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {subscriptionData ? (
                                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-4">
                                    <div className="flex items-center gap-2 text-primary mb-2">
                                        <CheckCircle className="w-5 h-5" />
                                        <span className="font-semibold text-lg">Active Subscription</span>
                                    </div>
                                    <div className="space-y-1 text-sm text-gray-400">
                                        <div className="flex justify-between">
                                            <span>Seats:</span>
                                            <span className="text-white font-medium">{subscriptionData.purchased_seats}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>{subscriptionData.cancel_at_period_end ? " Expiry Date: " : " Renewal Date: "}</span>
                                            <span className="text-white font-medium">{formatDate(subscriptionData.current_period_end)}</span>
                                        </div>
                                    </div>

                                    {!subscriptionData.cancel_at_period_end && subscriptionData.subscription_type === "stripe" && (
                                        <Button
                                            variant="destructive"
                                            onClick={handleCancelSubscription}
                                            className="w-full mt-4 bg-red-600/20 text-red-500 border border-red-500/50 hover:bg-red-600 hover:text-white transition-all duration-300"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    Cancelling...
                                                </>
                                            ) : (
                                                "Cancel Subscription"
                                            )}
                                        </Button>
                                    )}


                                </div>
                            ) : Number(userCount) >= 1000 ? (
                                <Button
                                    onClick={() => setShowInquiryForm(true)}
                                    className="w-full bg-primary hover:bg-btnHover text-white border-0 py-6 text-lg"
                                >
                                    Contact for Organization Plan
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleCreateSubscription}
                                    className="w-full bg-primary hover:bg-btnHover text-white border-0 py-6 text-lg"
                                    disabled={loading || Number(userCount) < 5}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Please wait
                                        </>
                                    ) : (
                                        "Get Started"
                                    )}
                                </Button>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    )
}